import { skipToken } from "@reduxjs/toolkit/query";
import { LoadingState } from "@repo/app-types/client/client";
import { isTeammateDeactivated } from "@repo/app-types/entities/teammate";
import * as Sentry from "@sentry/react";
import type { Session } from "@supabase/supabase-js";
import { posthog } from "posthog-js";
import { useEffect, useRef, useState, type FC } from "react";
import { useBootQuery, useUserQuery } from "../api/entities/user.js";
import { getIsPopupAuthCallback } from "../api/popupAuth.js";
import { isUnauthorizedQueryError } from "../api/queryErrors.js";
import { getSupabaseClient } from "../api/supabaseClient.js";
import { applyBootState, signoutUser } from "../api/supabaseService.js";
import { setCompany } from "../features/company/companySlice.js";
import {
  getIsOAuthIntegrationCallback,
  OAuthPopupCallbackPage,
  subscribeToIntegrationConnected,
} from "../features/integrations/oauthPopupCallbackPage.js";
import { McpOAuthConsentPage } from "../features/mcpOauth/mcpOAuthConsentPage.js";
import { AccountResolution } from "../features/onboardingForm/accountResolution.js";
import { OnboardingNameFormContainer } from "../features/onboardingForm/onboardingNameFormContainer.js";
import { AuthPopupCallbackPage } from "../features/signin/authPopupCallbackPage.js";
import { InvitationSignInPage } from "../features/signin/invitationSignInPage.js";
import { SignInPage } from "../features/signin/signInPage.js";
import { setTeammate } from "../features/teammate/teammateSlice.js";
import { useAppDispatch } from "../hooks/hook.js";
import { useRefetchOnFocusIfStale } from "../hooks/useRefetchOnFocusIfStale.js";
import { DeactivatedPage } from "../pages/deactivatedPage.js";
import { AdminRouterProvider } from "./adminRouterProvider.js";
import {
  FullScreenLoader,
  FullScreenMessage,
  NotAuthorizedScreen,
  SessionLoadStalledScreen,
} from "./appStatusScreens.js";
import { selectAdminRouteVariant } from "./routeTrees.js";
import { router, teamMemberRouter } from "./routes.js";

/** How old the boot data may get before a window focus refetches it. */
const BOOT_STALE_TIME_MS = 30 * 60 * 1000;

/**
 * How long the session may sit in the loading state before we assume it's
 * wedged (e.g. a frozen tab holding the auth lock) and offer a recovery prompt.
 * Longer than the auth lock's own acquire timeout so the lock fallback gets a
 * chance to recover on its own first.
 */
const SESSION_LOAD_STALL_TIMEOUT_MS = 20 * 1000;

/** The invitation token from an `/accept-invitation?t=...` deep link, if any. */
function readCompanyInvitationTokenFromUrl(): string | null {
  if (window.location.pathname !== "/accept-invitation") {
    return null;
  }
  return new URLSearchParams(window.location.search).get("t");
}

const App: FC = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoadingState, setSessionLoadingState] = useState<LoadingState>(
    LoadingState.LOADING,
  );
  const [hasSessionLoadStalled, setHasSessionLoadStalled] = useState(false);
  const dispatch = useAppDispatch();

  const [companyInvitationToken] = useState<string | null>(
    readCompanyInvitationTokenFromUrl,
  );

  useEffect(() => {
    const supabase = getSupabaseClient();

    // Detect OTP params.
    const url = new URL(window.location.href);
    const otpTokenHash =
      url.searchParams.get("type") === "email"
        ? url.searchParams.get("token_hash")
        : null;

    if (otpTokenHash) {
      // Try to perform OTP auth.
      void supabase.auth
        .verifyOtp({
          type: "email",
          token_hash: otpTokenHash,
        })
        .catch((error: unknown) => {
          console.error("OTP verification failed:", error);
        });

      // Strip OTP params to prevent them from being reused.
      url.searchParams.delete("type");
      url.searchParams.delete("token_hash");
      window.history.replaceState(null, "", url.toString());
    }

    // Fetch the current session AND subscribe to auth state changes. Account
    // resolution is driven by the `getUser` RTK Query below.
    const {
      data: { subscription },
    } = getSupabaseClient().auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setSessionLoadingState(LoadingState.LOADED);

      if (!session) {
        // Clear the user in Sentry if we don't have a session.
        Sentry.setUser(null);
        posthog.reset();
      }
    });

    // Return the unsubscribe function to cleanup on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Watchdog: if the session never leaves the loading state, it's almost always
  // a wedged auth lock (a frozen tab holding it). Surface a recovery prompt and
  // report it — this class of hang otherwise produces no error and no telemetry.
  useEffect(() => {
    if (sessionLoadingState !== LoadingState.LOADING) {
      setHasSessionLoadStalled(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setHasSessionLoadStalled(true);
      Sentry.captureMessage(
        "Session load stalled: Supabase auth did not resolve within the timeout",
        "error",
      );
    }, SESSION_LOAD_STALL_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [sessionLoadingState]);

  const loginCacheKey = session
    ? `${session.user.id}:${session.user.last_sign_in_at ?? session.expires_at ?? "unknown"}`
    : null;

  // Scope account resolution to the current login. No polling for now — a
  // successful resolution action refetches explicitly.
  const {
    currentData: userData,
    isLoading: isUserLoading,
    isError: isUserError,
    error: userError,
    refetch: refetchUser,
  } = useUserQuery(
    loginCacheKey
      ? {
          loginCacheKey,
          invitationToken: companyInvitationToken,
        }
      : skipToken,
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const isResolved = userData?.status === "resolved";

  // Heavy app data — only once the session is fully resolved. The same login
  // key prevents a fresh authentication from rendering an older boot payload.
  const {
    currentData: bootData,
    isError: isBootError,
    fulfilledTimeStamp: bootFulfilledTimeStamp,
    refetch: refetchBoot,
  } = useBootQuery(loginCacheKey && isResolved ? loginCacheKey : skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // Refetch the boot on window focus, but only once it's gone stale.
  useRefetchOnFocusIfStale(
    refetchBoot,
    bootFulfilledTimeStamp,
    BOOT_STALE_TIME_MS,
  );

  // The OAuth popup announces a newly connected integration once it lands back
  // on our origin. The integrations slice is only ever populated from the boot
  // payload, so refetch it to pick the integration up without a page reload.
  useEffect(
    () =>
      subscribeToIntegrationConnected(() => {
        void refetchBoot();
      }),
    [refetchBoot],
  );

  // Push teammate/company into Redux for resolved + onboarding states so the
  // rest of the app (selectors) keeps working unchanged.
  useEffect(() => {
    if (
      userData &&
      (userData.status === "resolved" || userData.status === "needs_onboarding")
    ) {
      dispatch(setTeammate(userData.teammate));
      dispatch(setCompany(userData.company));
    }
  }, [userData, dispatch]);

  const [bootApplied, setBootApplied] = useState(false);
  useEffect(() => {
    if (!isResolved) {
      // Block rendering on the next resolution until its boot is applied.
      setBootApplied(false);
      return;
    }
    if (bootData) {
      dispatch(applyBootState(bootData));
      setBootApplied(true);
    }
  }, [isResolved, bootData, dispatch]);

  // Mid-session invalidation: if the resolved company changes (or the session
  // is no longer resolved after having been), hard-reload so company-scoped
  // Redux state can't leak across the change.
  const resolvedCompanyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!userData) return;
    const currentCompanyId =
      userData.status === "resolved" || userData.status === "needs_onboarding"
        ? userData.company.id
        : null;
    const previousCompanyId = resolvedCompanyIdRef.current;
    if (previousCompanyId !== null && currentCompanyId !== previousCompanyId) {
      window.location.reload();
      return;
    }
    if (currentCompanyId !== null) {
      resolvedCompanyIdRef.current = currentCompanyId;
    }
  }, [userData]);

  if (getIsPopupAuthCallback()) {
    return <AuthPopupCallbackPage />;
  }

  if (getIsOAuthIntegrationCallback()) {
    return <OAuthPopupCallbackPage />;
  }

  if (sessionLoadingState === LoadingState.LOADING) {
    if (hasSessionLoadStalled) {
      return (
        <SessionLoadStalledScreen onReload={() => window.location.reload()} />
      );
    }
    return <FullScreenLoader />;
  }

  if (sessionLoadingState === LoadingState.ERROR) {
    return (
      <FullScreenMessage>
        There was an error loading your session. Please refresh and try again.
      </FullScreenMessage>
    );
  }

  // If we've loaded our session call and there's no session, show a sign in/sign up prompt
  if (!session) {
    // An invitation deep link gets a dedicated screen that offers only the auth
    // methods the invited company accepts.
    if (companyInvitationToken) {
      return <InvitationSignInPage invitationToken={companyInvitationToken} />;
    }
    return <SignInPage />;
  }

  // Session present — resolve the account via GET /user.
  if (isUserLoading || (!userData && !isUserError)) {
    return <FullScreenLoader />;
  }

  if (isUserError || !userData) {
    if (isUnauthorizedQueryError(userError)) {
      return <NotAuthorizedScreen onSignOut={() => void signoutUser()} />;
    }
    return (
      <FullScreenMessage>
        There was an error loading your session. Please refresh and try again.
      </FullScreenMessage>
    );
  }

  if (userData.status === "unresolved") {
    return (
      <AccountResolution
        options={userData.options}
        invitationToken={companyInvitationToken}
        refetchUser={refetchUser}
      />
    );
  }

  const teammate = userData.teammate;

  // Show deactivated page if the teammate has been deactivated
  if (isTeammateDeactivated(teammate)) {
    return <DeactivatedPage />;
  }

  // MCP OAuth consent (Supabase redirects the browser here mid-flow). Gated to
  // exactly the users the MCP resource server accepts: an active teammate with a
  // company. By this point `unresolved` (no company) and deactivated teammates
  // have already been handled above, and `needs_onboarding` users — who already
  // belong to a company and so get a valid MCP token — are intentionally allowed
  // through (placed before the onboarding gate). Letting an ineligible user
  // approve would hand the client a token every tool call then rejects.
  // `unresolved` users fall through to account resolution above and reach this
  // screen once resolved (the pathname persists). No boot payload needed.
  if (window.location.pathname === "/mcp/oauth/authorize") {
    return <McpOAuthConsentPage />;
  }

  if (userData.status === "needs_onboarding") {
    return (
      <OnboardingNameFormContainer teammate={teammate} onSaved={refetchUser} />
    );
  }

  // Resolved — wait for the boot payload to be applied before rendering the app.
  if (isBootError) {
    return (
      <FullScreenMessage>
        There was an error loading your profile. Please refresh and try again.
      </FullScreenMessage>
    );
  }

  if (!bootApplied) {
    return <FullScreenLoader />;
  }

  const activeRouter = selectAdminRouteVariant(teammate, {
    standard: router,
    teamMember: teamMemberRouter,
  });

  return <AdminRouterProvider router={activeRouter} />;
};

export default App;
